<script setup>
/**
 * "What it may do" — the four things the assistant may do to a chat besides replying.
 *
 * THIS WAS A PAGE (`Settings -> Permissions`) UNTIL THE OPTION-B MERGE. It is a section
 * now, and a COMPONENT rather than markup pasted into `PersonalityPage.vue`, for one
 * reason that is worth stating because it is the whole risk of that merge:
 *
 *   THE SWITCHES IN HERE WRITE IMMEDIATELY. The page hosting them does not — it is draft
 *   state behind a Save bar. The defence is that this half is visibly, structurally its
 *   own thing: its own heading, its own explanatory line and its own errors. Its switches
 *   write instantly; its notes and ticks commit through the page's one Save bar (P-5.1).
 *
 *   WHAT IS **NOT** TRUE, AND WAS WRITTEN HERE UNTIL W-10: that nothing in here is ever
 *   "waiting to be saved". A card's NOTE AND TICKS are draft state exactly like the form
 *   above, and while the page believed otherwise its Save bar said "Everything is saved"
 *   over an unsaved card and its leave guard let the tab close on one. So this section
 *   reports its unsaved cards upwards (`dirtyKeys`) and can commit them on request
 *   (`saveAll`), and the page combines both halves into one unsaved state. The switches
 *   are still the section's own business and still write on their own.
 *
 * FOUR CARDS, EACH WRITING ITS OWN ROW. These are four unrelated settings that happen to
 * share a screen, so turning off "close a chat" writes `{ enabled }` for that card alone
 * and never commits a half-typed note under "pass a chat to someone". The notes and ticks
 * have ONE commit point, the page's Save bar (`saveAll`, below); the cards used to carry
 * a Save each, and three models for one screen is what P-5.1 removed.
 *
 * THE TARGETS READ IS THE ONLY THING HERE THAT CAN FAIL BY ITSELF. It reaches Chatwoot
 * with the CALLER's own session, so it goes stale independently of everything else — and
 * the old UI's answer to that was an empty tick list, pixel-identical to "you have chosen
 * nothing", whose Save wiped the real choices. Here it degrades to one state on two cards
 * and the saves from those cards simply do not carry a list (see `AllowlistEditor.vue`).
 *
 * NOTE WHAT IS NOT FETCHED WHEN NOTHING NEEDS IT: if no permission declares
 * `needsChoices`, the Chatwoot read is never made at all.
 */
import { computed, ref, watch } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import PermissionCard from './PermissionCard.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { wasloApi } from '../api/client.js';

const { t } = useWasloI18n();
const accountId = useWasloAccountId();

const permissions = useWasloApi(
  signal => wasloApi.getPermissions(accountId.value, signal),
  { isEmpty: () => false }
);

// `immediate: false` — started only once we know something needs it, from the first
// successful read of the permissions themselves.
const targets = useWasloApi(
  signal => wasloApi.getPermissionTargets(accountId.value, signal),
  { immediate: false, isEmpty: () => false }
);

const rows = computed(() => permissions.data.value ?? []);

const needsTargets = computed(() => rows.value.some(row => row.needsChoices));

const isFetching = permissions.isFetching;

const errorMessage = computed(() => permissions.errorMessage.value);

const loadTargets = () => {
  if (needsTargets.value) targets.reload();
};

/**
 * The second read starts only once the first one says something needs it — a watcher
 * rather than a call inside the loader, because the answer is not known until the
 * permissions have arrived, and a render is not a place to start a request from.
 */
watch(needsTargets, needed => {
  if (needed) targets.reload();
});

/** `'loading' | 'ready' | 'unreadable'` — the state every chooser branches on. */
const targetsState = computed(() => {
  if (targets.error.value) return 'unreadable';
  if (!targets.hasLoaded.value) return 'loading';
  return 'ready';
});

const targetsData = computed(() =>
  targetsState.value === 'ready' ? targets.data.value : null
);

// ── the writes ─────────────────────────────────────────────────────────────────────
// One key at a time, never one flag for the page: two cards written in quick succession
// must not make each other look busy, and a failure on one must not remount the other's
// switch.
const togglingKey = ref(null);
const cardErrors = ref({});
const switchEpoch = ref(0);

const replaceRow = saved => {
  permissions.data.value = rows.value.map(row =>
    row.key === saved.key ? saved : row
  );
};

const clearError = key => {
  if (cardErrors.value[key]) {
    const next = { ...cardErrors.value };
    delete next[key];
    cardErrors.value = next;
  }
};

// ── what the cards are holding ─────────────────────────────────────────────────────
// Each card announces its own pending patch, or `null` when it has nothing waiting. The
// section keeps the latest per key: it is what the page's Save bar counts as unsaved, and
// what `saveAll` commits without having to reach inside a card.
const draftByKey = ref({});

const setDraft = (key, patch) => {
  const next = { ...draftByKey.value };
  if (patch && Object.keys(patch).length > 0) next[key] = patch;
  else delete next[key];
  draftByKey.value = next;
};

// Row order, not insertion order: the page names these to the owner, and the order she
// reads them in is the order they are on screen.
const dirtyKeys = computed(() =>
  rows.value.map(row => row.key).filter(key => key in draftByKey.value)
);

const onToggle = async (permission, next) => {
  if (togglingKey.value !== null) {
    // DROPPED, NOT QUEUED — and `Switch` has already flipped its own copy on the click, so
    // without this the control sits in a state nothing on the server agrees with. Same
    // remount, same reason as a failed write.
    switchEpoch.value += 1;
    return;
  }
  togglingKey.value = permission.key;
  clearError(permission.key);
  try {
    // Render from the response, never from what we sent: the BFF echoes the saved row,
    // including whether it can actually fire.
    replaceRow(
      await wasloApi.updatePermission(accountId.value, permission.key, {
        enabled: next,
      })
    );
  } catch {
    cardErrors.value = {
      ...cardErrors.value,
      [permission.key]: t('WASLO.PERSONALITY.PERMISSIONS.SWITCH_FAILED'),
    };
    // `Switch` flips its own copy on click whether or not we agreed. Remounting every
    // card's control is the one reliable way to snap the failed one back to the truth.
    switchEpoch.value += 1;
  } finally {
    togglingKey.value = null;
  }
};

/**
 * One card's note and ticks, written. Reports whether it landed instead of announcing it,
 * because the page's Save bar raises one message for the whole page and must not raise
 * four.
 */
const writeCard = async (permission, patch) => {
  if (Object.keys(patch).length === 0) return true;
  clearError(permission.key);
  try {
    replaceRow(
      await wasloApi.updatePermission(accountId.value, permission.key, patch)
    );
    return true;
  } catch {
    cardErrors.value = {
      ...cardErrors.value,
      [permission.key]: t('WASLO.PERSONALITY.PERMISSIONS.SAVE_FAILED'),
    };
    return false;
  }
};

/**
 * Commit every card that is holding something, for the page's Save bar. Sequential, one
 * key at a time: the BFF's PATCH is per permission, so there is nothing to gain from
 * racing them and a half-written row to lose. Each failure is also reported on its own card.
 *
 * IT ANSWERS WITH BOTH LISTS, not with a boolean, because the page has to tell the owner
 * what DID land: one card failing out of three does not make "nothing was changed" true,
 * and that sentence is the one she would act on by writing it all again.
 *
 * A `reduce` over a promise rather than a loop, because the repo bans `for…of`: the chain
 * is what makes it sequential, and the accumulator is what landed so far.
 */
const saveAll = async () =>
  // A snapshot: `dirtyKeys` empties itself as each saved row comes back.
  [...dirtyKeys.value].reduce(
    async (previous, key) => {
      const outcome = await previous;
      const row = rows.value.find(item => item.key === key);
      const patch = draftByKey.value[key];
      if (!row || !patch) return outcome;
      return (await writeCard(row, patch))
        ? { ...outcome, saved: [...outcome.saved, key] }
        : { ...outcome, failed: [...outcome.failed, key] };
    },
    Promise.resolve({ saved: [], failed: [] })
  );

const reload = () => permissions.reload();

// The page reads both: `dirtyKeys` to know it has something unsaved on it, `saveAll` to
// commit it from the one Save button an owner can actually see.
defineExpose({ dirtyKeys, saveAll });
</script>

<template>
  <section
    class="flex flex-col gap-4"
    :data-waslo-permissions="rows.length"
    data-waslo-permissions-section
  >
    <SettingsHeader
      :heading="t('WASLO.PERSONALITY.PERMISSIONS.HEADING')"
      :description="t('WASLO.PERSONALITY.PERMISSIONS.DESCRIPTION')"
    />

    <!--
      A section cannot borrow the page frame's error state without claiming the whole page
      failed, which would be false — the persona above it loaded fine. So this half owns a
      banner of its own and the rest of the page carries on.
    -->
    <Banner
      v-if="errorMessage"
      color="ruby"
      :action-label="t('WASLO.COMMON.RETRY')"
      data-waslo-permissions-error
      @action="reload"
    >
      {{ errorMessage }}
    </Banner>

    <div v-else-if="isFetching" class="flex flex-col gap-4" aria-busy="true">
      <div
        v-for="n in 2"
        :key="n"
        class="h-24 rounded-xl bg-n-alpha-1 animate-pulse"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <PermissionCard
        v-for="permission in rows"
        :key="permission.key"
        :permission="permission"
        :targets-state="targetsState"
        :targets="targetsData"
        :is-toggling="togglingKey === permission.key"
        :switch-epoch="switchEpoch"
        :save-error="cardErrors[permission.key] || ''"
        @toggle="next => onToggle(permission, next)"
        @dirty="patch => setDraft(permission.key, patch)"
        @retry-targets="loadTargets"
      />
    </div>
  </section>
</template>
